import { BhagCounterConfigs } from 'types/types';
import {
  CASCADE_MAX_ROLL_MS,
  CASCADE_MIN_ROLL_MS,
  CASCADE_ROLL_FRACTION,
  CONTINUOUS_SPIN_MAX_S,
  DIGITS_PER_REVOLUTION,
  MS_PER_SECOND,
  REEL_DIGIT_HEIGHT,
} from './constants';
import { FullCounterConfigs } from './types';

export const isValidConfigs = (
  configs?: BhagCounterConfigs
): configs is FullCounterConfigs =>
  typeof configs !== 'undefined' &&
  typeof configs.value === 'number' &&
  typeof configs.incrementValue === 'number' &&
  typeof configs.lastUpdatedAt === 'string';

/*
 * en-US, not en-ZA: the South African locale formats as "1 999 999,00"
 * (space groups, comma decimal). en-US gives the comma-grouped style we want.
 */
const groupedNumber = new Intl.NumberFormat('en-US');

export const digitCountOf = (value: number) => value.toString().length;

const secondsSince = (isoTimestamp: string) => {
  const then = new Date(isoTimestamp).getTime();
  if (isNaN(then)) return 0;
  return Math.max(0, (Date.now() - then) / MS_PER_SECOND);
};

export const computeInitialValue = ({
  value,
  incrementValue,
  lastUpdatedAt,
}: FullCounterConfigs) =>
  Math.round(value + secondsSince(lastUpdatedAt) * incrementValue);

export const buildTemplate = (digitCount: number) => {
  if (digitCount <= 0) return '0';
  return groupedNumber.format(10 ** digitCount - 1);
};

export type TemplateToken =
  | { kind: 'digit'; place: number; visualIndex: number }
  | { kind: 'separator'; char: string; visualIndex: number };

export const tokenizeTemplate = (template: string): TemplateToken[] => {
  const digitCount = template.replace(/\D/g, '').length;
  let digitIdx = 0;

  return template.split('').map((char, idx) => {
    const visualIndex = idx + 1;
    if (/\d/.test(char)) {
      digitIdx += 1;
      const place = digitCount - digitIdx;
      return { kind: 'digit', place, visualIndex };
    }
    return { kind: 'separator', char, visualIndex };
  });
};

export const reelTransform = (position: number) =>
  `translateY(calc(${position} * -${REEL_DIGIT_HEIGHT}em))`;

// value of a single step at `place`: 1 for units, 10 for tens, 100...
const placeValue = (place: number) => DIGITS_PER_REVOLUTION ** place;

export const digitAtPlace = (value: number, place: number) =>
  Math.floor(value / placeValue(place)) % DIGITS_PER_REVOLUTION;

/**
 * How many digit steps a reel at `place` makes per second: it advances one digit
 * per `placeValue(place)` of value, so at `incrementValue`/s it steps this fast.
 */
export const reelDigitsPerSecond = (place: number, incrementValue: number) =>
  incrementValue / placeValue(place);

/**
 * Seconds for a reel at `place` to complete one full revolution — i.e. all
 * DIGITS_PER_REVOLUTION digit steps — at the given pace.
 */
const secondsPerRevolution = (place: number, incrementValue: number) =>
  DIGITS_PER_REVOLUTION / reelDigitsPerSecond(place, incrementValue);

/**
 * Fast reels read as an intentional blur, so we let them spin continuously.
 * Slower reels would sit frozen mid-digit, so those cascade instead.
 */
export const spinsContinuously = (place: number, incrementValue: number) =>
  incrementValue > 0 &&
  secondsPerRevolution(place, incrementValue) <= CONTINUOUS_SPIN_MAX_S;

/**
 * Continuous position (0..DIGITS_PER_REVOLUTION) of a reel for a given value.
 * The fractional part is what gives the smooth analogue look: e.g. for 1234 the
 * tens reel sits at 3.4, 40% of the way from 3 to 4. The live value is never
 * negative, so a plain modulo suffices.
 */
export const phaseOf = (value: number, place: number) =>
  (value / placeValue(place)) % DIGITS_PER_REVOLUTION;

/**
 * How long a single digit step takes for a cascading reel. The reel carries once
 * every `MS_PER_SECOND / reelDigitsPerSecond` ms; we roll for a fraction of that
 * so it rests in between, capped so very slow reels still snap briskly.
 */
export const cascadeStepMs = (place: number, incrementValue: number) => {
  const carryIntervalMs =
    MS_PER_SECOND / reelDigitsPerSecond(place, incrementValue);
  return Math.min(
    Math.max(carryIntervalMs * CASCADE_ROLL_FRACTION, CASCADE_MIN_ROLL_MS),
    CASCADE_MAX_ROLL_MS
  );
};
